import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { mdiDeleteForeverOutline, mdiProgressClock } from '@mdi/js'
import type { ErasureItem, ErasureRequest, PersonalData } from '../../api/schema'
import { useCan } from '../../contexts/PermissionsContext'
import {
    ApiError,
    ERASURE_DECISIONS,
    ERASURE_GROUNDS,
    needsGround,
    useCreateErasureRequest,
    useCreateSubjectErasureRequest,
    useErasurePreview,
    useErasureRequests,
    useExecuteErasureRequest,
    useExtendRetention,
    useUpdateErasureRequest,
    type ErasureDecisionInput,
} from '../../hooks/useDataProtection'
import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import { Alert, AlertDescription, AlertTitle } from '../ui/alert'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Checkbox } from '../ui/checkbox'
import { AppIcon } from '../ui/icon'
import { Input } from '../ui/input'

const selectClass = 'h-9 rounded-md border border-border bg-background px-2 text-sm'

/**
 * The Purge action of a document (spec 2026-10-03-02, lot 4), distinct from
 * Delete. The dialog shows the erasure preview: every store with its action
 * and count, the stores the product does not control, the holds that forbid
 * it. Confirming opens a manual request decided at once and executes it.
 *
 * A bundle child (spec 2026-10-04-05) names its bundle: the bundle is purged
 * with it when it has no other live child, kept otherwise (the erasure is then
 * reported not covered) unless the user purges the whole bundle, with a ground
 * and a note for the other documents.
 */
export function DocumentPurgeAction({
    org,
    docUid,
    origin = 'manual',
    compact = false,
    onPurged,
}: {
    org: string
    docUid: string
    origin?: 'manual' | 'retention'
    compact?: boolean
    onPurged?: () => void
}) {
    const { t } = useTranslation('data-protection')
    const canWrite = useCan('data_protection.write')
    const [open, setOpen] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const preview = useErasurePreview(org, docUid, open)
    const create = useCreateErasureRequest(org)
    const update = useUpdateErasureRequest(org)
    const execute = useExecuteErasureRequest(org)
    const [wholeBundle, setWholeBundle] = useState(false)
    const [ground, setGround] = useState('')
    const [note, setNote] = useState('')
    if (!canWrite) return null
    const busy = create.isPending || update.isPending || execute.isPending
    const bundle = preview.data?.bundle
    const canPurgeWhole = !!bundle && !bundle.held && bundle.other_children.length > 0
    const groundMissing = wholeBundle && (!ground || !note.trim())
    const blocked = (preview.data?.blocked ?? true) || groundMissing
    const bundleKind = bundle ? t(`erasure.bundle_kind.${bundle.kind}`, { pages: bundle.pages.join(', ') }) : ''

    const purge = async () => {
        setError(null)
        try {
            let request = await create.mutateAsync({ origin, document_uid: docUid, decision: 'purge' })
            // The bundle and its other children are items of the same request.
            const pending = request.items.filter((i) => !i.decision)
            if (pending.length > 0) {
                const decisions: ErasureDecisionInput[] = pending.map((i) => {
                    const decision = (wholeBundle && i.kind === 'document' ? 'purge' : i.proposal) as ErasureDecisionInput['decision']
                    return needsGround(i.proposal, decision) ? { key: i.key, decision, ground, note } : { key: i.key, decision }
                })
                request = await update.mutateAsync({ uid: request.uid, body: { decisions } })
            }
            await execute.mutateAsync(request.uid)
            setOpen(false)
            onPurged?.()
        } catch (e) {
            setError((e as Error).message)
        }
    }

    return (
        <>
            <Button
                variant="outline"
                className={compact ? 'h-8 px-2 text-destructive' : 'p-2 text-destructive hover:text-destructive/80'}
                title={t('erasure.purge')}
                onClick={() => setOpen(true)}
                data-testid={`document-purge-btn-${docUid}`}
            >
                <AppIcon path={mdiDeleteForeverOutline} size="md" />
                {compact && <span className="ml-1 text-xs">{t('erasure.purge')}</span>}
            </Button>
            <AlertDialog open={open} onOpenChange={setOpen}>
                <AlertDialogContent className="max-w-2xl" data-testid="document-purge-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('erasure.purge_title')}</AlertDialogTitle>
                        <AlertDialogDescription>{t('erasure.purge_description')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    {preview.isLoading && <p className="text-sm text-muted-foreground">…</p>}
                    {preview.data && (
                        <div className="space-y-3 text-sm">
                            {preview.data.blocked && (
                                <Alert variant="warning" data-testid="document-purge-blocked">
                                    <AlertDescription>
                                        {preview.data.holds.length > 0
                                            ? t('erasure.blocked_hold')
                                            : t('erasure.blocked_children', { count: preview.data.live_children })}
                                    </AlertDescription>
                                </Alert>
                            )}
                            {bundle && (
                                <Alert variant="warning" data-testid="document-purge-bundle">
                                    <AlertDescription>
                                        {bundle.held
                                            ? t('erasure.bundle_kept_hold', { name: bundle.name, kind: bundleKind })
                                            : bundle.other_children.length > 0
                                              ? t('erasure.bundle_kept_children', {
                                                  name: bundle.name,
                                                  kind: bundleKind,
                                                  count: bundle.other_children.length,
                                                  list: bundle.other_children.map((c) => c.name).join(', '),
                                              })
                                              : t('erasure.bundle_purged', { name: bundle.name, kind: bundleKind })}
                                    </AlertDescription>
                                </Alert>
                            )}
                            {canPurgeWhole && (
                                <div className="space-y-2">
                                    <label className="flex items-center gap-2">
                                        <Checkbox
                                            checked={wholeBundle}
                                            onCheckedChange={setWholeBundle}
                                            data-testid="document-purge-bundle-whole"
                                        />
                                        {t('erasure.bundle_whole')}
                                    </label>
                                    {wholeBundle && (
                                        <div className="flex flex-wrap items-center gap-2">
                                            <p className="w-full text-xs text-muted-foreground">{t('erasure.bundle_whole_ground')}</p>
                                            <select
                                                className={selectClass}
                                                value={ground}
                                                onChange={(e) => setGround(e.target.value)}
                                                data-testid="document-purge-bundle-ground"
                                            >
                                                <option value="" disabled>{t('erasure.ground_label')}</option>
                                                {ERASURE_GROUNDS.map((g) => (
                                                    <option key={g} value={g}>{t(`erasure.ground.${g}`)}</option>
                                                ))}
                                            </select>
                                            <Input
                                                className="h-9 flex-1 min-w-48"
                                                placeholder={t('erasure.note')}
                                                value={note}
                                                onChange={(e) => setNote(e.target.value)}
                                                data-testid="document-purge-bundle-note"
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                            <ul className="max-h-64 overflow-auto divide-y border rounded-md" data-testid="document-purge-stores">
                                {preview.data.stores.map((s) => (
                                    <li key={s.name} className="flex items-center gap-2 px-3 py-1" data-testid={`purge-store-${s.name}`}>
                                        <span className="flex-1 font-mono text-xs">{s.name}</span>
                                        <Badge variant={s.action === 'uncontrolled' ? 'warning' : 'secondary'}>
                                            {t(`erasure.action.${s.action}`)}
                                        </Badge>
                                        <span className="w-10 text-right tabular-nums">{s.count ?? '—'}</span>
                                    </li>
                                ))}
                            </ul>
                            {preview.data.recipients.length > 0 && (
                                <p data-testid="document-purge-recipients">
                                    {t('erasure.recipients', {
                                        list: preview.data.recipients
                                            .map((r) => `${t(`disclosures.channel.${r.channel}`, { defaultValue: r.channel })} (${r.count})`)
                                            .join(', '),
                                    })}
                                </p>
                            )}
                            {preview.data.uncontrolled.length > 0 && (
                                <p className="text-muted-foreground" data-testid="document-purge-uncontrolled">
                                    {t('erasure.uncontrolled', { stores: preview.data.uncontrolled.join(', ') })}
                                </p>
                            )}
                        </div>
                    )}
                    {error && <p className="text-sm text-destructive" data-testid="document-purge-error">{error}</p>}
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="document-purge-cancel">{t('common:actions.cancel')}</AlertDialogCancel>
                        <Button
                            variant="destructive"
                            disabled={blocked || busy}
                            onClick={() => void purge()}
                            data-testid="document-purge-confirm"
                        >
                            {t('erasure.purge_confirm')}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    )
}

/**
 * Banner of a document being purged (`erasure_state = purging`): the run in
 * progress, or a failed run with its error and a Retry, which executes the
 * failed request again. The request is read with `data_protection.read`; a
 * person's request is retried from the contact's page.
 */
export function DocumentErasureBanner({ org, docUid, onPurged }: { org: string; docUid: string; onPurged?: () => void }) {
    const { t } = useTranslation('data-protection')
    const canRead = useCan('data_protection.read')
    const canWrite = useCan('data_protection.write')
    const requests = useErasureRequests(org, { document_uid: docUid }, canRead)
    const execute = useExecuteErasureRequest(org)
    const [error, setError] = useState<string | null>(null)
    const latest = requests.data?.[0]
    const done = useRef(false)

    useEffect(() => {
        if (latest?.status === 'completed' && !done.current) {
            done.current = true
            onPurged?.()
        }
    }, [latest?.status, onPurged])

    const retry = async () => {
        setError(null)
        try {
            await execute.mutateAsync(latest!.uid)
        } catch (e) {
            setError((e as Error).message)
        }
    }

    if (latest?.status === 'failed') {
        return (
            <Alert variant="destructive" className="mb-4" data-testid="document-erasure-failed">
                <AppIcon path={mdiDeleteForeverOutline} size="sm" />
                <AlertTitle>{t('erasure.failed_title')}</AlertTitle>
                <AlertDescription>
                    <p>{t('erasure.failed_body')}</p>
                    {latest.report?.error && (
                        <p className="font-mono text-xs break-all" data-testid="document-erasure-error">{latest.report.error}</p>
                    )}
                    {error && <p data-testid="document-erasure-retry-error">{error}</p>}
                    {canWrite && (
                        <Button
                            variant="outline"
                            className="mt-2 h-8"
                            disabled={execute.isPending}
                            onClick={() => void retry()}
                            data-testid="document-erasure-retry"
                        >
                            {t('erasure.retry')}
                        </Button>
                    )}
                </AlertDescription>
            </Alert>
        )
    }
    return (
        <Alert variant="warning" className="mb-4" data-testid="document-erasure-running">
            <AppIcon path={mdiProgressClock} size="sm" />
            <AlertTitle>{t('erasure.running_title')}</AlertTitle>
            <AlertDescription>{t('erasure.running_body')}</AlertDescription>
        </Alert>
    )
}

/** What an item points at, from the person's inventory (the request holds uids only). */
function itemLabel(item: ErasureItem, pd: PersonalData | undefined, t: (k: string) => string): string {
    if (item.kind === 'document') {
        return pd?.documents.find((d) => d.uid === item.uid)?.name ?? item.type_code ?? item.uid
    }
    if (item.kind === 'extracted_value') return item.type_code ?? item.uid
    return t('erasure.record')
}

type Draft = Record<string, ErasureDecisionInput>

/**
 * A person's erasure request on the "Personal data" section of a contact:
 * per item, the facts, the proposal, the DPO's decision and its ground.
 */
export function ContactErasureRequest({ org, userUid, personalData }: { org: string; userUid: string; personalData?: PersonalData }) {
    const { t, i18n } = useTranslation('data-protection')
    const canWrite = useCan('data_protection.write')
    const requests = useErasureRequests(org, { subject_user_uid: userUid })
    const create = useCreateSubjectErasureRequest(org, userUid)
    const update = useUpdateErasureRequest(org)
    const execute = useExecuteErasureRequest(org)
    const [receivedAt, setReceivedAt] = useState(() => new Date().toISOString().slice(0, 10))
    const [channel, setChannel] = useState<'email' | 'letter' | 'web' | 'phone' | 'other'>('email')
    const [draft, setDraft] = useState<Draft>({})
    const [message, setMessage] = useState<string | null>(null)
    const [recipientsNote, setRecipientsNote] = useState<string | null>(null)

    const current: ErasureRequest | undefined = useMemo(
        () => requests.data?.find((r) => r.status !== 'rejected'),
        [requests.data],
    )
    useEffect(() => setDraft({}), [current?.uid, current?.status])
    const fmt = (d?: string | null) => (d ? new Date(d).toLocaleDateString(i18n.language) : '—')

    const run = async (fn: () => Promise<unknown>) => {
        setMessage(null)
        try {
            await fn()
        } catch (e) {
            setMessage(e instanceof ApiError && e.code === 'erasure_facts_changed' ? t('erasure.facts_changed') : (e as Error).message)
        }
    }

    const setDecision = (item: ErasureItem, patch: Partial<ErasureDecisionInput>) => {
        setDraft((d) => {
            const prev = d[item.key] ?? { key: item.key, decision: (item.decision ?? item.proposal) as ErasureDecisionInput['decision'] }
            return { ...d, [item.key]: { ...prev, ...patch } }
        })
    }

    if (!current) {
        if (!canWrite) return null
        return (
            <div className="mt-4 rounded-md border p-3" data-testid="contact-erasure-new">
                <h3 className="text-sm font-medium mb-2">{t('erasure.request_title')}</h3>
                <div className="flex flex-wrap items-end gap-2">
                    <label className="text-xs text-muted-foreground">
                        {t('erasure.received_at')}
                        <Input type="date" value={receivedAt} onChange={(e) => setReceivedAt(e.target.value)} className="h-9" data-testid="contact-erasure-received-at" />
                    </label>
                    <label className="text-xs text-muted-foreground">
                        {t('erasure.channel_label')}
                        <select className={`${selectClass} block`} value={channel} onChange={(e) => setChannel(e.target.value as typeof channel)} data-testid="contact-erasure-channel">
                            {(['email', 'letter', 'web', 'phone', 'other'] as const).map((c) => (
                                <option key={c} value={c}>{t(`erasure.channel.${c}`)}</option>
                            ))}
                        </select>
                    </label>
                    <Button
                        onClick={() => void run(() => create.mutateAsync({ received_at: new Date(receivedAt).toISOString(), channel }))}
                        disabled={create.isPending}
                        data-testid="contact-erasure-create"
                    >
                        {t('erasure.create')}
                    </Button>
                </div>
                {message && <p className="text-sm text-destructive mt-2" data-testid="contact-erasure-message">{message}</p>}
            </div>
        )
    }

    const editable = canWrite && (current.status === 'pending_decision' || current.status === 'approved')
    const decisions = Object.values(draft)

    return (
        <div className="mt-4 rounded-md border p-3" data-testid="contact-erasure-request">
            <div className="flex flex-wrap items-center gap-2 mb-2">
                <h3 className="text-sm font-medium">{t('erasure.request_title')}</h3>
                <Badge variant="secondary" data-testid="contact-erasure-status">{t(`erasure.status.${current.status}`)}</Badge>
                {current.deadline_at && (
                    <span className="text-xs text-muted-foreground" data-testid="contact-erasure-deadline">
                        {t('erasure.deadline', { date: fmt(current.extended_until ?? current.deadline_at) })}
                    </span>
                )}
            </div>
            <ul className="divide-y border rounded-md" data-testid="contact-erasure-items">
                {current.items.map((item) => {
                    const d = draft[item.key]
                    const decision = d?.decision ?? item.decision ?? ''
                    const ground = needsGround(item.proposal, decision)
                    return (
                        <li key={item.key} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm" data-testid={`erasure-item-${item.uid}`}>
                            <Badge variant="outline">{t(`erasure.kind.${item.kind}`)}</Badge>
                            <span className="flex-1 min-w-40 truncate">{itemLabel(item, personalData, t)}</span>
                            {item.facts.filter((f) => !f.startsWith('entity:')).map((f) => (
                                <Badge key={f} variant="secondary" className="text-xs">{t(`erasure.fact.${f.split(':')[0]}`, { value: f.split(':')[1] })}</Badge>
                            ))}
                            <span className="text-xs text-muted-foreground" data-testid={`erasure-item-proposal-${item.uid}`}>
                                {t('erasure.proposal', { decision: t(`erasure.decision.${item.proposal}`) })}
                            </span>
                            {editable ? (
                                <select
                                    className={selectClass}
                                    value={decision}
                                    onChange={(e) => setDecision(item, { decision: e.target.value as ErasureDecisionInput['decision'] })}
                                    data-testid={`erasure-item-decision-${item.uid}`}
                                >
                                    <option value="" disabled>{t('erasure.decide')}</option>
                                    {(ERASURE_DECISIONS[item.kind] ?? []).map((o) => (
                                        <option key={o} value={o}>{t(`erasure.decision.${o}`)}</option>
                                    ))}
                                </select>
                            ) : (
                                <Badge data-testid={`erasure-item-outcome-${item.uid}`}>
                                    {item.decision ? t(`erasure.decision.${item.decision}`) : '—'}
                                </Badge>
                            )}
                            {editable && ground && (
                                <>
                                    <select
                                        className={selectClass}
                                        value={d?.ground ?? item.ground ?? ''}
                                        onChange={(e) => setDecision(item, { ground: e.target.value })}
                                        data-testid={`erasure-item-ground-${item.uid}`}
                                    >
                                        <option value="" disabled>{t('erasure.ground_label')}</option>
                                        {ERASURE_GROUNDS.map((g) => (
                                            <option key={g} value={g}>{t(`erasure.ground.${g}`)}</option>
                                        ))}
                                    </select>
                                    <Input
                                        className="h-9 w-48"
                                        placeholder={t('erasure.note')}
                                        value={d?.note ?? item.note ?? ''}
                                        onChange={(e) => setDecision(item, { note: e.target.value })}
                                        data-testid={`erasure-item-note-${item.uid}`}
                                    />
                                </>
                            )}
                        </li>
                    )
                })}
            </ul>
            {current.report && current.status === 'completed' && (
                <p className="text-sm mt-2" data-testid="contact-erasure-report">
                    {current.report.not_covered ? t('erasure.completed_not_covered') : t('erasure.completed')}
                </p>
            )}
            {current.status === 'completed' && current.report?.uncontrolled?.includes('bundle_file') && (
                <p className="text-sm mt-1" data-testid="contact-erasure-report-bundle">{t('erasure.completed_bundle')}</p>
            )}
            {current.status === 'failed' && (
                <div className="flex flex-wrap items-center gap-2 mt-2">
                    <p className="text-sm text-destructive" data-testid="contact-erasure-failed">{t('erasure.failed')}</p>
                    {canWrite && (
                        <Button
                            variant="outline"
                            className="h-8"
                            disabled={execute.isPending}
                            onClick={() => void run(() => execute.mutateAsync(current.uid))}
                            data-testid="contact-erasure-retry"
                        >
                            {t('erasure.retry')}
                        </Button>
                    )}
                </div>
            )}
            {canWrite && current.status !== 'rejected' && (
                <div className="flex flex-wrap items-center gap-2 mt-3">
                    <Input
                        className="h-9 flex-1 min-w-60"
                        placeholder={t('erasure.recipients_note')}
                        value={recipientsNote ?? current.report?.recipients_note ?? ''}
                        onChange={(e) => setRecipientsNote(e.target.value)}
                        data-testid="contact-erasure-recipients-note"
                    />
                    <Button
                        variant="outline"
                        disabled={recipientsNote === null || update.isPending}
                        onClick={() => void run(async () => {
                            await update.mutateAsync({ uid: current.uid, body: { recipients_note: recipientsNote ?? '' } })
                            setRecipientsNote(null)
                        })}
                        data-testid="contact-erasure-recipients-save"
                    >
                        {t('erasure.save_recipients')}
                    </Button>
                </div>
            )}
            {message && <p className="text-sm text-destructive mt-2" data-testid="contact-erasure-message">{message}</p>}
            {editable && (
                <div className="flex flex-wrap gap-2 mt-3">
                    <Button
                        variant="outline"
                        disabled={!decisions.length || update.isPending}
                        onClick={() => void run(() => update.mutateAsync({ uid: current.uid, body: { decisions } }))}
                        data-testid="contact-erasure-save"
                    >
                        {t('erasure.save_decisions')}
                    </Button>
                    <Button
                        variant="destructive"
                        disabled={current.status !== 'approved' || decisions.length > 0 || execute.isPending}
                        onClick={() => void run(() => execute.mutateAsync(current.uid))}
                        data-testid="contact-erasure-execute"
                    >
                        {t('erasure.execute')}
                    </Button>
                    <Button
                        variant="ghost"
                        disabled={update.isPending}
                        onClick={() => void run(() => update.mutateAsync({ uid: current.uid, body: { reject: true } }))}
                        data-testid="contact-erasure-reject"
                    >
                        {t('erasure.reject')}
                    </Button>
                </div>
            )}
        </div>
    )
}

/** The queue's "Extend": a hold with reason retention_extension until a date. */
export function ExtendRetentionAction({ org, documentUid, userUid }: { org: string; documentUid?: string; userUid?: string }) {
    const { t } = useTranslation('data-protection')
    const canWrite = useCan('data_protection.write')
    const extend = useExtendRetention(org)
    const [open, setOpen] = useState(false)
    const [endsAt, setEndsAt] = useState('')
    const [note, setNote] = useState('')
    const [error, setError] = useState<string | null>(null)
    if (!canWrite) return null
    const id = documentUid ?? userUid ?? ''
    const submit = async () => {
        setError(null)
        try {
            await extend.mutateAsync({ document_uid: documentUid, user_uid: userUid, ends_at: endsAt, note: note || undefined })
            setOpen(false)
        } catch (e) {
            setError((e as Error).message)
        }
    }
    return (
        <>
            <Button variant="outline" className="h-8 px-2 text-xs" onClick={() => setOpen(true)} data-testid={`dp-queue-extend-${id}`}>
                {t('erasure.extend')}
            </Button>
            <AlertDialog open={open} onOpenChange={setOpen}>
                <AlertDialogContent data-testid="dp-extend-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('erasure.extend_title')}</AlertDialogTitle>
                        <AlertDialogDescription>{t('erasure.extend_description')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <Input type="date" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} data-testid="dp-extend-ends-at" />
                    <Input placeholder={t('erasure.note')} value={note} onChange={(e) => setNote(e.target.value)} data-testid="dp-extend-note" />
                    {error && <p className="text-sm text-destructive" data-testid="dp-extend-error">{error}</p>}
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="dp-extend-cancel">{t('common:actions.cancel')}</AlertDialogCancel>
                        <Button disabled={!endsAt || extend.isPending} onClick={() => void submit()} data-testid="dp-extend-confirm">
                            {t('erasure.extend')}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    )
}

/** The queue's "Anonymize" on a due contact: a one-item retention request, decided on the contact's page. */
export function AnonymizeContactAction({ org, userUid, onCreated }: { org: string; userUid: string; onCreated: () => void }) {
    const { t } = useTranslation('data-protection')
    const canWrite = useCan('data_protection.write')
    const create = useCreateErasureRequest(org)
    if (!canWrite) return null
    return (
        <Button
            variant="outline"
            className="h-8 px-2 text-xs text-destructive"
            disabled={create.isPending}
            onClick={() => void create.mutateAsync({ origin: 'retention', user_uid: userUid }).then(onCreated)}
            data-testid={`dp-queue-anonymize-${userUid}`}
        >
            {t('erasure.anonymize')}
        </Button>
    )
}

/** The DPO screen's requests: origin, status, deadline. */
export function ErasureRequestsSection({ org }: { org: string }) {
    const { t, i18n } = useTranslation('data-protection')
    const requests = useErasureRequests(org)
    const fmt = (d?: string | null) => (d ? new Date(d).toLocaleDateString(i18n.language) : '—')
    return (
        <section className="mb-6" data-testid="dp-requests">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t('erasure.requests_title')}</h2>
            {!requests.data?.length ? (
                <p className="text-sm text-muted-foreground" data-testid="dp-requests-empty">{t('erasure.requests_empty')}</p>
            ) : (
                <div className="border rounded-lg divide-y">
                    {requests.data.map((r) => (
                        <div key={r.uid} className="flex flex-wrap items-center gap-3 px-4 py-2 text-sm" data-testid={`dp-request-${r.uid}`}>
                            <Badge variant="outline">{t(`erasure.origin.${r.origin}`)}</Badge>
                            <Badge variant={r.status === 'failed' ? 'destructive' : 'secondary'} data-testid={`dp-request-status-${r.uid}`}>
                                {t(`erasure.status.${r.status}`)}
                            </Badge>
                            {r.subject_user_uid ? (
                                <Link to="/$org/users/$userUid" params={{ org, userUid: r.subject_user_uid }} className="flex-1 hover:underline" data-testid={`dp-request-link-${r.uid}`}>
                                    {t('erasure.kind.person')} · {t('erasure.items', { count: r.items.length })}
                                </Link>
                            ) : (
                                <span className="flex-1">{t('erasure.kind.document')}</span>
                            )}
                            {r.report?.not_covered && <Badge variant="warning">{t('erasure.not_covered')}</Badge>}
                            <span className="text-xs text-muted-foreground">
                                {r.deadline_at ? t('erasure.deadline', { date: fmt(r.extended_until ?? r.deadline_at) }) : fmt(r.created_at)}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </section>
    )
}
